import Handlebars from "handlebars";
import Papa from "papaparse";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import RichTextEditor from "../components/RichTextEditor";
import { EmailTemplate } from "../models/notification";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";
import { Toast } from "../services/toast-service";
type ImportEmailRecipientsProps = {
  userInfo: UserInfo;
};

const ImportEmailRecipients: React.FC<ImportEmailRecipientsProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [recipientsCSVFile, setRecipientsCSVFile] = useState<File | undefined>(
    undefined,
  );
  const [fromEmail, setFromEmail] = useState<string>("");
  const [replyToEmail, setReplyToEmail] = useState<string>("");
  const [csvError, setCsvError] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>();
  const [compiledEmailTemplate, setCompiledEmailTemplate] =
    useState<EmailTemplate>();

  const navigate = useNavigate();

  // fetch email templates
  const [emailTemplates, setEmailTemplates] = useState<{ name: string }[]>([]);
  useEffect(() => {
    const fetchEmailTemplates = async () => {
      setLoading(true);
      notificationService
        .getEmailTemplates(true)
        .then((templates) => {
          setEmailTemplates(templates);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    compileEmailTemplate(emailTemplate);
  }, [emailTemplate]);

  const getEmailTemplate = (name: string) => {
    setLoading(true);
    notificationService
      .getEmailTemplate(name)
      .then((template) => {
        setEmailTemplate(template);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateCSVForEmails = (file: File) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const rows = results.data;
          const containsEmail = rows.some(
            (row: any) =>
              row.student_email &&
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.student_email),
          );
          resolve(containsEmail);
        },
        error: (error) => reject(error),
      });
    });
  };

  const handleRecipientsCSVFileChange = async (e: {
    target: HTMLInputElement;
  }) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setRecipientsCSVFile(file);
      const containsEmail = await validateCSVForEmails(file);
      setCsvError(!containsEmail);
    } else {
      setRecipientsCSVFile(undefined);
      setCsvError(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!loading) {
      try {
        if (!recipientsCSVFile) {
          throw new Error("Recipients have not been added.");
        }
        if (csvError) {
          throw new Error(
            "CSV file must contain at least one valid email address.",
          );
        }
        setLoading(true);
        await notificationService.importEmailRecipients({
          emailSubject: emailTemplate?.subject || "",
          recipientsCSV: recipientsCSVFile,
          emailTemplate: emailTemplate?.name || "",
          emailTemplateVariables: {
            Salutation: "Hi",
          },
          fromEmail: fromEmail,
          replyToEmail: replyToEmail,
        });
        Toast(
          "Success!!",
          "The recipients have been successfully imported.",
          "success",
        );
        navigate("/edit");
      } catch (err: unknown) {
        console.error("Error saving notification:", err);

        let errorMessage = "Something went wrong saving notification.";

        if (notificationService.isHttpError(err)) {
          const httpError: any = err;
          if (httpError.response?.data?.message) {
            errorMessage = httpError.response.data.message;
          } else if (httpError.message) {
            errorMessage = httpError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        Toast("Error!!", errorMessage, "danger");
      }
      setLoading(false);
    }
  };

  const compileEmailTemplate = (template?: EmailTemplate) => {
    if (!template) {
      return;
    }
    const complied = Handlebars.compile(template.html)({
      Salutation: "Hi",
      UnitOfStudyCode: "{{unit_code}}",
      UnitOfStudyName: "{{unit_name}}",
      PreferredName: "{{preferred_name}}",
      UOSCName: "{{uosc_name}}",
    });
    const compliedSubject = Handlebars.compile(template.subject)({
      UnitOfStudyCode: "{{unit_code}}",
      UnitOfStudyName: "{{unit_name}}",
    });
    setCompiledEmailTemplate({
      ...template,
      html: complied,
      subject: compliedSubject,
    });
  };

  const handleChangeEmailTemplate = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    if (e.target.value) {
      getEmailTemplate(e.target.value);
    } else {
      setEmailTemplate(undefined);
      setCompiledEmailTemplate(undefined);
    }
  };

  const disabled = !recipientsCSVFile || csvError || !emailTemplate;
  return (
    <Layout userInfo={props.userInfo} loading={loading}>
      <form onSubmit={handleSubmit} className="flex flex-col pt-8 px-20">
        <div className="flex mb-4">
          <div className="w-2/3">
            <div className="flex flex-1 flex-col mr-10">
              <div className="flex flex-col pb-4">
                <label htmlFor="emailTemplate" className="text-sm pb-2">
                  Email template
                </label>
                <select
                  name="emailTemplate"
                  value={emailTemplate?.name}
                  onChange={handleChangeEmailTemplate}
                  className="border px-2 py-2 text-sm"
                >
                  <option value="">Select an email template</option>
                  {emailTemplates.map((template) => (
                    <option key={template.name} value={template.name}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col pb-4">
                <label className="text-sm pb-2">
                  From email (Optional: if the CSV has uosc_email column)
                </label>
                <input
                  type="text"
                  name="fromEmail"
                  value={fromEmail}
                  placeholder="e.g. Student Success <student.success@sydney.edu.au>"
                  onChange={(e) => setFromEmail(e.target.value)}
                  className="border px-2 py-1 text-sm"
                />
              </div>
              <div className="flex flex-col pb-4">
                <label className="text-sm pb-2">
                  Reply to email (Optional: if the CSV has uosc_email column)
                </label>
                <input
                  type="text"
                  name="replyToEmail"
                  placeholder="e.g. Student Success <student.success@sydney.edu.au>"
                  value={replyToEmail}
                  onChange={(e) => setReplyToEmail(e.target.value)}
                  className="border px-2 py-1 text-sm"
                />
              </div>
              {emailTemplate && (
                <div className="flex flex-col pb-4">
                  <label className="text-sm pb-2">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={compiledEmailTemplate?.subject}
                    disabled={true}
                    className="border px-2 py-1 text-sm"
                  />
                </div>
              )}

              {compiledEmailTemplate && (
                <div className="flex flex-col pb-4">
                  <label className="text-sm pb-2">Email template preview</label>
                  <RichTextEditor
                    // email template is for view only and not editable
                    readOnly={true}
                    value={compiledEmailTemplate?.html || ""}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="w-1/3 ">
            <div className="flex flex-row">
              <div className="flex flex-row mt-4">
                <div className="flex flex-1 flex-col">
                  <label className="text-sm pb-2">
                    Recipients (CSV file with columns uosc_name, uosc_email,
                    academic_year, session_code, unit_code, unit_name, unikey,
                    sid, preferred_name and student_email). <br />
                    Note: This UI has been designed to support approx 20k
                    entries.
                  </label>
                  <input
                    type="file"
                    id="fileRecipientsCSV"
                    name="fileRecipientsCSV"
                    onChange={handleRecipientsCSVFileChange}
                    accept=".csv"
                  />
                  {csvError && (
                    <p className="text-red-500 text-xs mt-2">
                      CSV file must contain at least one valid email address.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={disabled}
                    className={` text-white text-xs rounded px-5 py-2 mt-4
                        
                          ${
                            disabled
                              ? "cursor-not-allowed bg-[#ea654b]"
                              : "cursor-pointer bg-[#e64626]"
                          }`}
                  >
                    Import Recipients
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default ImportEmailRecipients;
